import {FatcaCrsCondition} from '@sollapay/enums';
import {Badge, Checkbox, Icon, Typography} from '@sollapay/ui/components';
import {cn} from '@sollapay/ui/lib';
import {useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import type {FatcaCrsFormValues} from '@/features/trust-accounts/activation/packages/compliance/utils';
import type {FC} from 'react';
import type {Control, UseFormSetValue} from 'react-hook-form';

const ALL_CONDITIONS = [
  FatcaCrsCondition.REAL_ESTATE_TRANSACTION,
  FatcaCrsCondition.FUNDED_BY_DEPOSIT,
  FatcaCrsCondition.SECURES_OBLIGATION,
  FatcaCrsCondition.ASSETS_PAID_TO_PARTIES,
  FatcaCrsCondition.NOT_MARGIN_ACCOUNT,
  FatcaCrsCondition.NOT_CREDIT_CARD_LINKED
] as const;

export interface FatcaCrsConditionsSectionProps {
  control: Control<FatcaCrsFormValues>;
  setValue: UseFormSetValue<FatcaCrsFormValues>;
  disabled?: boolean;
}

export const FatcaCrsConditionsSection: FC<FatcaCrsConditionsSectionProps> = ({
  control,
  setValue,
  disabled = false
}) => {
  const {t} = useTranslation('trustAccounts');

  const selectedConditions = useWatch({control, name: 'conditions'}) ?? [];
  const allSelected = selectedConditions.length === ALL_CONDITIONS.length;
  const someSelected = selectedConditions.length > 0;

  const handleSelectAll = () => {
    setValue('conditions', allSelected ? [] : [...ALL_CONDITIONS], {shouldDirty: true});
  };

  const toggleCondition = (condition: FatcaCrsCondition) => {
    const next = selectedConditions.includes(condition)
      ? selectedConditions.filter(c => c !== condition)
      : [...selectedConditions, condition];
    setValue('conditions', next, {shouldDirty: true});
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Typography weight="semibold" className="whitespace-normal">
          {t('activation.packages.compliance.fatcaCrs.conditions.heading')}
        </Typography>
        <Typography size="sm" color="tertiary" className="whitespace-normal">
          {t('activation.packages.compliance.fatcaCrs.conditions.supportingText')}
        </Typography>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        {/* Select-all header */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Checkbox
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onCheckedChange={handleSelectAll}
            disabled={disabled}
            className="shrink-0"
          />
          <Typography size="xs" weight="semibold" color="tertiary">
            {t('activation.packages.compliance.fatcaCrs.selectAll')}
          </Typography>
        </div>

        {/* Condition rows */}
        {ALL_CONDITIONS.map((condition, index) => {
          const isChecked = selectedConditions.includes(condition);
          return (
            <label
              key={condition}
              htmlFor={`fatca-condition-${condition}`}
              className={cn(
                'flex items-start gap-3 border-t border-border px-4 py-3 transition-colors',
                disabled ? 'cursor-default' : 'cursor-pointer',
                isChecked && 'bg-background-success-subtle'
              )}
            >
              <Checkbox
                id={`fatca-condition-${condition}`}
                checked={isChecked}
                onCheckedChange={() => toggleCondition(condition)}
                disabled={disabled}
                className="mt-0.5 shrink-0"
              />
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-center gap-2">
                  <Typography size="xs" weight="semibold" color="tertiary">
                    {t('activation.packages.compliance.fatcaCrs.conditionLabel', {
                      number: index + 1
                    })}
                  </Typography>
                  {isChecked && (
                    <Badge variant="success" dot size="sm">
                      {t('activation.packages.compliance.fatcaCrs.approvedBadge')}
                    </Badge>
                  )}
                </div>
                <Typography size="sm" className="whitespace-normal">
                  {t(
                    `activation.packages.compliance.fatcaCrs.conditions.${condition}` as Parameters<
                      typeof t
                    >[0]
                  )}
                </Typography>
              </div>
            </label>
          );
        })}

        {/* Footer hint */}
        <div className="flex items-center gap-2 border-t border-border px-4 py-3">
          {allSelected ? (
            <Icon name="check-circle" className="size-3.5 shrink-0 text-text-success" />
          ) : (
            <Icon name="help-circle" className="size-3.5 shrink-0 text-fg-tertiary" />
          )}
          <Typography size="xs" color="tertiary" className="whitespace-normal">
            {allSelected
              ? t('activation.packages.compliance.fatcaCrs.allConditionsConfirmed', {
                  count: ALL_CONDITIONS.length
                })
              : t('activation.packages.compliance.fatcaCrs.allConditionsHint')}
          </Typography>
        </div>
      </div>
    </div>
  );
};
